import React, { lazy, Suspense, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { DarkModeProvider, useDarkMode } from './context/DarkModeContext';
import { TooltipHost } from './components/Tooltip';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import GuestRoute from './components/GuestRoute';
import Loading from './pages/Loading';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Chat = lazy(() => import('./pages/Chat'));
const Notifications = lazy(() => import('./pages/Notifications'));
const HotelsSearch = lazy(() => import('./pages/Hotels'));
const HotelDetail = lazy(() => import('./pages/HotelDetail'));
const Tours = lazy(() => import('./pages/Tours'));
const TourDetail = lazy(() => import('./pages/TourDetail'));
const Booking = lazy(() => import('./pages/Booking'));
const Posts = lazy(() => import('./pages/Posts'));
const PostDetail = lazy(() => import('./pages/PostDetail'));

// Lazy load admin pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const HotelList = lazy(() => import('./pages/HotelList'));
const HotelForm = lazy(() => import('./pages/HotelForm'));
const BookingManagement = lazy(() => import('./pages/BookingManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const SystemData = lazy(() => import('./pages/SystemData'));
const CategoryManagement = lazy(() => import('./pages/CategoryManagement'));
const BedTypeManagement = lazy(() => import('./pages/BedTypeManagement'));
const ExtraTypeManagement = lazy(() => import('./pages/ExtraTypeManagement'));
const AmenityManagement = lazy(() => import('./pages/AmenityManagement'));
const VoucherManagement = lazy(() => import('./pages/VoucherManagement'));
const ReviewManagement = lazy(() => import('./pages/ReviewManagement'));
const PostManagement = lazy(() => import('./pages/PostManagement'));
const TourManagement = lazy(() => import('./pages/TourManagement'));
const RandomBookingManagement = lazy(() => import('./pages/RandomBookingManagement'));
const IncomeManagement = lazy(() => import('./pages/IncomeManagement'));
const Settings = lazy(() => import('./pages/Settings'));

// Loading component
const LoadingFallback = () => (
	<Box
		display="flex"
		justifyContent="center"
		alignItems="center"
		minHeight="100vh"
	>
		<CircularProgress size={ 60 } />
	</Box>
);

// Tạo MUI theme - Modern Design
const createAppTheme = (mode) => createTheme({
	palette: {
		primary: {
			main: '#3b82f6',
			light: '#dbeafe',
			dark: '#1e40af',
			contrastText: '#ffffff',
		},
		secondary: {
			main: '#8b5cf6',
			light: '#ede9fe',
			dark: '#6d28d9',
		},
		success: { main: '#10b981', light: '#d1fae5', dark: '#047857' },
		warning: { main: '#f59e0b', light: '#fef3c7', dark: '#d97706' },
		error: { main: '#ef4444', light: '#fee2e2', dark: '#dc2626' },
		info: { main: '#06b6d4', light: '#cffafe', dark: '#0e7490' },
		mode,
		background: {
			default: mode === 'dark' ? '#0f172a' : '#f8fafc',
			paper: mode === 'dark' ? '#1e293b' : '#ffffff',
		},
		text: {
			primary: mode === 'dark' ? '#f1f5f9' : '#1e293b',
			secondary: mode === 'dark' ? '#94a3b8' : '#64748b',
		},
		divider: mode === 'dark' ? '#334155' : '#e2e8f0',
	},
	typography: {
		fontFamily: "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
		h1: { fontWeight: 700 },
		h2: { fontWeight: 700 },
		h3: { fontWeight: 600 },
		h4: { fontWeight: 600 },
		h5: { fontWeight: 600 },
		h6: { fontWeight: 600 },
	},
	shape: {
		borderRadius: 12, // Modern rounded corners
	},
	shadows: [
		'none',
		'0 1px 2px 0 rgba(0, 0, 0, 0.05)',
		'0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
		'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
		'0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
		'0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
		'0 25px 50px -12px rgba(0, 0, 0, 0.25)',
	],
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					borderRadius: 10,
					fontWeight: 500,
					padding: '8px 20px',
					boxShadow: 'none',
					'&:hover': {
						boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
					},
				},
				contained: {
					'&:hover': {
						boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: 'none',
				},
				elevation1: {
					boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
				},
				elevation2: {
					boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
				},
				elevation3: {
					boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 16,
					boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
					border: '1px solid #f1f5f9',
					'&:hover': {
						boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-root': {
						borderRadius: 10,
						'& fieldset': {
							borderColor: '#e2e8f0',
						},
						'&:hover fieldset': {
							borderColor: '#cbd5e1',
						},
						'&.Mui-focused fieldset': {
							borderWidth: 2,
						},
					},
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					borderRadius: 16,
					boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					fontWeight: 500,
				},
			},
		},
	},
});

function AppContent() {
	const { darkMode } = useDarkMode();
	const theme = useMemo(() => createAppTheme(darkMode ? 'dark' : 'light'), [darkMode]);
	return (
		<ThemeProvider theme={ theme }>
			<CssBaseline />
			<TooltipHost />
			<AuthProvider>
				<SocketProvider>
					<Suspense fallback={ <LoadingFallback /> }>
						<Routes>
							{/* Guest-only routes - redirect to home nếu đã đăng nhập */ }
							<Route path="/login" element={ <GuestRoute><Login /></GuestRoute> } />
							<Route path="/register" element={ <GuestRoute><Register /></GuestRoute> } />
							<Route path="/forgot-password" element={ <GuestRoute><ForgotPassword /></GuestRoute> } />

							{/* Loading transition page (3s then redirect to ?next=) */ }
							<Route path="/loading" element={ <Loading /> } />

							{/* Protected routes with Layout - Yêu cầu đăng nhập */ }
							<Route
								path="/"
								element={
									<ProtectedRoute>
										<Layout />
									</ProtectedRoute>
								}
							>
								<Route index element={ <Home /> } />
								<Route path="hotels" element={ <HotelsSearch /> } />
								<Route path="hotels/:id" element={ <HotelDetail /> } />
								<Route path="tours" element={ <Tours /> } />
								<Route path="tours/:id" element={ <TourDetail /> } />
								<Route path="booking" element={ <Booking /> } />
								<Route path="posts" element={ <Posts /> } />
								<Route path="posts/:id" element={ <PostDetail /> } />
								<Route path="about" element={ <About /> } />
								<Route path="contact" element={ <Contact /> } />
								<Route path="profile" element={ <Profile /> } />
								<Route path="chat" element={ <Chat /> } />
								<Route path="notifications" element={ <Notifications /> } />
								<Route path="*" element={ <NotFound /> } />
							</Route>

							{/* Admin routes */ }
							<Route
								path="/admin"
								element={
									<AdminRoute>
										<AdminLayout />
									</AdminRoute>
								}
							>
								<Route index element={ <Navigate to="/admin/dashboard" replace /> } />
								<Route path="dashboard" element={ <Dashboard /> } />
								<Route path="hotels" element={ <HotelList /> } />
								<Route path="hotels/add" element={ <HotelForm /> } />
								<Route path="hotels/edit/:id" element={ <HotelForm /> } />
								<Route path="bookings" element={ <BookingManagement /> } />
								<Route path="users" element={ <UserManagement /> } />
								<Route path="categories" element={ <CategoryManagement /> } />
								<Route path="bed-types" element={ <BedTypeManagement /> } />
								<Route path="extra-types" element={ <ExtraTypeManagement /> } />
								<Route path="amenities" element={ <AmenityManagement /> } />
								<Route path="vouchers" element={ <VoucherManagement /> } />
								<Route path="reviews" element={ <ReviewManagement /> } />
								<Route path="posts" element={ <PostManagement /> } />
								<Route path="tours" element={ <TourManagement /> } />
								<Route path="random-bookings" element={ <RandomBookingManagement /> } />
								<Route path="incomes" element={ <IncomeManagement /> } />
								<Route path="system-data" element={ <SystemData /> } />
								<Route path="settings" element={ <Settings /> } />
							</Route>
						</Routes>
					</Suspense>
				</SocketProvider>
			</AuthProvider>
		</ThemeProvider>
	);
}

function App() {
	return (
		<Provider store={ store }>
			<DarkModeProvider>
				<AppContent />
			</DarkModeProvider>
		</Provider>
	);
}

export default App;
